import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Hourglass, PackageCheck } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { rp, fmtDate, PO_STATUS } from "@/lib/format";
import { useSeo, useShop } from "@/lib/shop";
import { GridSkeleton, Empty, ErrorState } from "@/components/site/States";
import { PoBadge, DemoBadge } from "@/components/site/Badges";
import { PoTermsBlock, FaqBlock } from "@/pages/InfoPages";

function PoCard({ p }) {
  const { addToCart } = useShop();
  const po = p.po_info;
  return (
    <div data-testid={`po-card-${p.slug}`} className="flex flex-col rounded-2xl border-2 border-neutral-900 bg-white overflow-hidden">
      <Link to={`/pre-order/${p.slug}`} className="relative block aspect-[4/3] overflow-hidden group">
        <img src={imgUrl(p.images?.[0])} alt={p.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute left-2 top-2 flex gap-1"><PoBadge status={po.status} />{p.is_demo && <DemoBadge />}</div>
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--bk-blue)]">{p.brand} · <span className="font-mono normal-case text-neutral-500">{p.sku}</span></div>
        <Link to={`/pre-order/${p.slug}`} className="font-heading font-bold leading-snug mt-1 line-clamp-2 hover:underline">{p.name}</Link>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="price text-xl font-extrabold">{rp(po.price)}</span>
          {po.normal_price && po.normal_price > po.price && <span className="text-xs line-through text-neutral-400">{rp(po.normal_price)}</span>}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <dt className="text-neutral-500">DP</dt><dd className="font-semibold text-right">{po.dp_per_unit ? rp(po.dp_per_unit) : "Tanpa DP"}</dd>
          <dt className="text-neutral-500">Pelunasan</dt><dd className="font-semibold text-right">{rp(po.remaining_per_unit)}</dd>
          <dt className="text-neutral-500">Tutup PO</dt><dd className="font-semibold text-right">{fmtDate(po.close_at)}</dd>
          <dt className="text-neutral-500">Estimasi tiba</dt><dd className="font-semibold text-right">{po.eta || "Menyusul"}</dd>
          {po.quota_left !== null && <><dt className="text-neutral-500">Sisa kuota</dt><dd className="font-semibold text-right">{po.quota_left}</dd></>}
        </dl>
        <div className="mt-4 flex gap-2 pt-1 mt-auto">
          <Link to={`/pre-order/${p.slug}`} data-testid={`po-detail-btn-${p.slug}`} className="flex-1 h-10 inline-flex items-center justify-center rounded-lg border-2 border-neutral-900 text-sm font-semibold hover:bg-neutral-900 hover:text-white transition-colors">Detail</Link>
          {po.orderable && <button data-testid={`po-order-btn-${p.slug}`} onClick={() => addToCart(p, "po")} className="flex-1 h-10 rounded-lg bg-[var(--bk-yellow)] border-2 border-neutral-900 text-sm font-semibold hover:bg-[var(--bk-orange)]">Pesan PO</button>}
        </div>
      </div>
    </div>
  );
}

function MiniList({ title, icon: Icon, items, empty, testid, field }) {
  return (
    <div className="rounded-2xl border-2 border-neutral-900 bg-white p-5" data-testid={testid}>
      <h3 className="font-heading font-bold flex items-center gap-2"><Icon className="h-5 w-5 text-[var(--bk-orange)]" />{title}</h3>
      <ul className="mt-3 divide-y">
        {items.slice(0, 4).map((p) => (
          <li key={p.id}><Link to={`/pre-order/${p.slug}`} className="flex items-center gap-3 py-2 hover:bg-neutral-50 rounded">
            <img src={imgUrl(p.images?.[0])} alt="" loading="lazy" className="h-12 w-12 rounded-lg object-cover border" />
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{p.name}</div><div className="text-xs text-neutral-500">{field(p)}</div></div>
          </Link></li>
        ))}
        {!items.length && <li className="text-sm text-neutral-500 py-2">{empty}</li>}
      </ul>
    </div>
  );
}

export default function PoCorner() {
  useSeo("Pre-Order Corner", "Daftar Pre-Order Bengkel Toys & Hobbies: status PO, DP, jadwal tutup, dan estimasi kedatangan.");
  const [f, setF] = useState({ status: "", category: "", brand: "", q: "", sort: "newest" });
  const [q, setQ] = useState("");
  const { data: banner } = useQuery({ queryKey: ["banners", "po"], queryFn: () => api.get("/banners", { params: { placement: "po" } }).then((r) => r.data[0]) });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => api.get("/brands").then((r) => r.data) });
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["po", f], queryFn: () => api.get("/po", { params: f }).then((r) => r.data) });
  const { data: all } = useQuery({ queryKey: ["po", {}], queryFn: () => api.get("/po").then((r) => r.data) });
  const allItems = all?.items || [];
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <div data-testid="po-corner-page">
      <section className="relative overflow-hidden bg-neutral-900 text-white border-b-2 border-neutral-900">
        {banner?.image_desktop ? <img src={imgUrl(banner.image_desktop)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" /> : <div className="absolute inset-y-0 right-0 w-1/2 bg-[var(--bk-blue)] stripes" />}
        <div className="absolute -left-20 -bottom-24 h-72 w-72 rounded-full bg-[var(--bk-orange)]" />
        <div className="relative container-x py-12 sm:py-16">
          <span className="rise inline-block text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-md bg-[var(--bk-red)]">Pre-Order</span>
          <h1 className="rise rise-2 font-display text-4xl sm:text-6xl mt-4 text-[var(--bk-yellow)] [text-shadow:3px_3px_0_#000]">{banner?.title || "PRE-ORDER CORNER"}</h1>
          <p className="rise rise-3 mt-4 max-w-2xl text-neutral-200">{banner?.subtitle}</p>
        </div>
      </section>

      <div className="container-x py-8">
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <MiniList testid="po-closing-soon-list" title="PO Segera Ditutup" icon={Hourglass} items={allItems.filter((p) => p.po_info.status === "closing_soon")} empty="Tidak ada PO yang segera ditutup." field={(p) => `Tutup ${fmtDate(p.po_info.close_at)}`} />
          <MiniList testid="po-arriving-list" title="Segera Tersedia" icon={PackageCheck} items={allItems.filter((p) => ["in_transit", "arrived"].includes(p.po_info.status))} empty="Belum ada PO dalam pengiriman." field={(p) => `${p.po_info.status_label} · ${p.po_info.eta || "-"}`} />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="po-status-tabs">
          {[["", "Semua"], ...Object.entries(PO_STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
            <button key={k} data-testid={`po-filter-${k || "all"}`} onClick={() => set("status", k)} className={`shrink-0 h-10 px-4 rounded-full border-2 border-neutral-900 text-sm font-semibold ${f.status === k ? "bg-neutral-900 text-white" : "bg-white hover:bg-[var(--bk-yellow)]"}`}>
              {l}{k && all ? ` (${all.counts[k] || 0})` : ""}
            </button>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <form onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }}>
            <input data-testid="po-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk PO..." className="w-full h-11 rounded-lg border-2 border-neutral-300 px-3 text-sm focus:border-neutral-900 outline-none" />
          </form>
          <select data-testid="po-category-select" value={f.category} onChange={(e) => set("category", e.target.value)} className="h-11 rounded-lg border-2 border-neutral-300 px-3 text-sm bg-white">
            <option value="">Semua kategori</option>{cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
          </select>
          <select data-testid="po-brand-select" value={f.brand} onChange={(e) => set("brand", e.target.value)} className="h-11 rounded-lg border-2 border-neutral-300 px-3 text-sm bg-white">
            <option value="">Semua merek</option>{brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
          <select data-testid="po-sort-select" value={f.sort} onChange={(e) => set("sort", e.target.value)} className="h-11 rounded-lg border-2 border-neutral-300 px-3 text-sm bg-white">
            <option value="newest">PO terbaru</option><option value="closing">Segera ditutup</option><option value="price_asc">Harga terendah</option>
          </select>
        </div>

        <div className="mt-6">
          {isLoading ? <GridSkeleton n={6} /> : isError ? <ErrorState onRetry={refetch} /> : data.items.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{data.items.map((p) => <PoCard key={p.id} p={p} />)}</div>
          ) : <Empty title="Belum ada produk PO" text="Tidak ada produk PO yang sesuai filter. Pantau terus Instagram kami untuk info PO terbaru." />}
        </div>

        <section className="mt-14 grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="font-heading text-2xl font-extrabold mb-4 flex items-center gap-2"><CalendarClock className="h-6 w-6 text-[var(--bk-orange)]" />Cara Pesan PO</h2>
            <ol className="space-y-3 text-sm">
              {["Pilih produk dengan status PO Dibuka atau PO Segera Ditutup.", "Tekan Pesan PO, lalu buka keranjang dan isi data pemesanan.", "Pesanan tersimpan dengan nomor referensi, lalu kirim pesan WhatsApp ke admin.", "Bayar DP sesuai arahan admin. Status pembayaran diubah setelah diverifikasi.", "Saat barang Tiba di Toko, admin akan menghubungi untuk pelunasan dan pengambilan/pengiriman."].map((t, i) => (
                <li key={i} className="flex gap-3"><span className="h-7 w-7 shrink-0 rounded-full bg-[var(--bk-yellow)] border-2 border-neutral-900 grid place-items-center font-bold text-xs">{i + 1}</span><span className="pt-1">{t}</span></li>
              ))}
            </ol>
            <div className="mt-8"><FaqBlock /></div>
          </div>
          <PoTermsBlock />
        </section>
      </div>
    </div>
  );
}
